import * as React from 'react';
import { Breadcrumb, IBreadcrumbItem, IBreadcrumbProps } from 'office-ui-fabric-react/lib/Breadcrumb';
import { css } from 'office-ui-fabric-react/lib/Utilities';
import { displayNameFacet, parentFolderItemFacet } from '../../facets/ItemFacets';
import { openItem } from '../../operations/openItem';
import { withCurrentItem, IDecorateWithCurrentItemProps } from '../decorators/selectors';
import { connectToItemCache } from '../decorators/connectToItemCache';
import { IItemCache } from '../../models/IItemCacheObserver';
import { itemsBreadcrumbControl } from '../../controls/itemsBreadcrumbControl';
import { connectToItemsScope, IDecorateConnectToItemsScopeProps } from '../decorators/connectToItemsScope';
import { preferredKeyFacetFacet } from '../../facets/OperationFacets';
import { IDecoratedProps } from '../../utilities/components/Decorators';

/**
 * @public
 */
export interface IItemsBreadcrumbBaseProps {
    itemKey: string | undefined;
    itemsScopeItemKey: string;
    onUpdateBreadcrumb?: (props: IBreadcrumbProps) => void;
    rootItemKey?: string;
    itemDisplayTextMap?: { [itemKey: string]: string };
}

/**
 * @public
 */
export type IItemsBreadcrumbProps = IDecorateWithCurrentItemProps<
    IDecorateConnectToItemsScopeProps<
    IDecoratedProps<IBreadcrumbProps, Pick<IBreadcrumbProps, 'items'>, IItemsBreadcrumbBaseProps>
    >
    >;

/**
 * @public
 */
export const ItemsBreadcrumb: React.ComponentType<IItemsBreadcrumbProps> = withCurrentItem()(
    connectToItemsScope()(
        connectToItemCache((
            itemCache: IItemCache,
            props: IItemsBreadcrumbBaseProps): Pick<IBreadcrumbProps, 'items'> => {
            const {
                itemsScopeItemKey,
                onUpdateBreadcrumb,
                rootItemKey,
                itemDisplayTextMap,
                itemKey
            } = props;

            const { dispatch } = itemCache;

            const createOpenItem = (nextItemKey: string) => () => dispatch(openItem({
                itemsScopeItemKey: itemsScopeItemKey,
                currentItemKey: nextItemKey
            }));

            let items: IBreadcrumbItem[] = [];

            const controlHandler = itemCache.demandItemFacet(itemsBreadcrumbControl, itemKey);

            const {
                getBreadcrumbItems
            } = itemsBreadcrumbControl.evaluate(controlHandler)(itemCache, {
                itemsScopeItemKey: itemsScopeItemKey,
                itemKey: itemKey,
                createOpenItem: createOpenItem,
                rootItemKey: rootItemKey
            });

            // Add displayText map for the prefered key.
            if (itemDisplayTextMap) {
                for (const key of Object.keys(itemDisplayTextMap)) {
                    const preferredKeyFacet = itemCache.demandItemFacet(preferredKeyFacetFacet, key);
                    const preferredValue = preferredKeyFacet && itemCache.demandItemFacet(preferredKeyFacet, key);
                    const preferredItemKey = preferredValue && preferredKeyFacet && preferredKeyFacet.serialize(preferredValue);
                    if (preferredItemKey) {
                        itemDisplayTextMap[preferredItemKey] = itemDisplayTextMap[key];
                    }
                }
            }

            let isResolved = true;
            if (getBreadcrumbItems) {
                items = getBreadcrumbItems();

                if (itemDisplayTextMap) {
                    items = items.map((item: IBreadcrumbItem) => {
                        return itemDisplayTextMap[item.key] !== undefined ?
                            {
                                ...item,
                                text: itemDisplayTextMap[item.key]
                            } :
                            item;
                    });
                }
            } else {
                ({
                    items,
                    isResolved
                } = createDefaultCrumbs(itemCache, {
                    itemKey: itemKey,
                    createOpenItem: createOpenItem,
                    itemDisplayTextMap: itemDisplayTextMap,
                    rootItemKey: rootItemKey
                }));
            }

            if (onUpdateBreadcrumb && isResolved) {
                onUpdateBreadcrumb({ items: items });
            }
            return {
                items: items
            };
        })((props: IBreadcrumbProps) => {
            return (
                <Breadcrumb { ...props } className={ css(props.items && props.items.length > 1 && 'hasMultiCrumbs', props.className) } />
            );
        })
    )
);

function createDefaultCrumbs(itemCache: IItemCache, props: {
    itemKey: string | undefined;
    createOpenItem: (itemKey: string) => () => void;
    itemDisplayTextMap: IItemsBreadcrumbBaseProps['itemDisplayTextMap'];
    rootItemKey?: string;
}): {
        items: IBreadcrumbItem[];
        isResolved: boolean;
    } {
    const {
        createOpenItem,
        rootItemKey,
        itemDisplayTextMap
    } = props;

    let currentItemKey = props.itemKey;

    let preferredRootItemKey: string | undefined = undefined;
    if (rootItemKey) {
        const preferredKeyFacet = itemCache.demandItemFacet(preferredKeyFacetFacet, rootItemKey);
        const preferredValue = preferredKeyFacet && itemCache.demandItemFacet(preferredKeyFacet, rootItemKey);
        preferredRootItemKey = preferredValue && preferredKeyFacet && preferredKeyFacet.serialize(preferredValue);
    }

    const items: IBreadcrumbItem[] = [];

    let isResolved = true;

    const itemKeys: {
        [itemKey: string]: boolean;
    } = {};

    while (currentItemKey && !itemKeys[currentItemKey]) {
        itemKeys[currentItemKey] = true;

        if (!itemCache.isItemFacetResolved(displayNameFacet, currentItemKey)) {
            isResolved = false;
        }

        const {
            displayName,
            parentFolder
        } = itemCache.demandItem({
            displayName: displayNameFacet,
            parentFolder: parentFolderItemFacet
        }, currentItemKey);

        items.unshift({
            key: currentItemKey,
            text: itemDisplayTextMap && itemDisplayTextMap[currentItemKey] !== undefined ?
                itemDisplayTextMap[currentItemKey] :
                (displayName || '\u200B'),
            onClick: createOpenItem(currentItemKey)
        });

        if (rootItemKey && (currentItemKey === rootItemKey || currentItemKey === preferredRootItemKey)) {
            break;
        }

        currentItemKey = parentFolder && parentFolder.itemKey || '';
    }

    return {
        items,
        isResolved
    };
}
